import { Activity, Cpu, Radio, Settings } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import type { ConfigurationProfileSchema } from '../../../../schemas/production/configuration-profile.schema';
import { Badge } from '../../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/form';
import { Input } from '../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Separator } from '../../../ui/separator';

export function SpecificNB2LoraConfigurationProfileForm() {
  const { control } = useFormContext<ConfigurationProfileSchema>();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cpu className="h-5 w-5" />
          Configurações Específicas - NB-2 LoRa
        </CardTitle>
        <CardDescription>Configure parâmetros específicos do equipamento NB-2 LoRa</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Configurações Básicas */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <h4 className="font-medium">Configurações Básicas</h4>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h5 className="text-sm font-medium mb-4">Parâmetros Gerais</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                control={control}
                name="config.specific.data_transmission_event"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Envio de mensagens em modo evento (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="60" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.time_to_sleep"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Definir tempo para iniciar modo sleep (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="7200" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.odometer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Definir Hodômetro</FormLabel>
                    <FormControl>
                      <Input placeholder="5000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.economy_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Modo de economia</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      defaultValue={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um modo de economia" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Modo 1 (GPS OFF e Rede ON em sleep)</SelectItem>
                        <SelectItem value="2">Modo 2 (GPS OFF e Rede OFF em sleep)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.lock_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de bloqueio</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      defaultValue={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um tipo de bloqueio" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Bloqueio Padrão</SelectItem>
                        <SelectItem value="2">Bloqueio invertido</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Configurações de Movimento */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <h4 className="font-medium">Configurações de Movimento e Sensores</h4>
            <Badge variant="outline" className="text-xs">
              Acelerômetro
            </Badge>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h5 className="text-sm font-medium mb-4">Detecção de Curva e Velocidade</h5>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="config.specific.heading_detection_angle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ângulo para Detecção de Curva</FormLabel>
                      <FormControl>
                        <Input placeholder="45" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="config.specific.speed_alert_threshold"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Limite de Alerta de Velocidade</FormLabel>
                      <FormControl>
                        <Input placeholder="120" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Configurações LoRa */}
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Radio className="h-4 w-4" />
            <h4 className="font-medium">Configurações LoRa</h4>
            <Badge variant="outline" className="text-xs">
              LoRaWAN / P2P
            </Badge>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h5 className="text-sm font-medium mb-4">Transmissão de Dados</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="config.specific.lorawan_data_transmission_event"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Envio de dados LoRaWAN em evento (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="60" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.p2p_data_transmission_event"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Envio de dados P2P em evento (seg)</FormLabel>
                    <FormControl>
                      <Input placeholder="60" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <h5 className="text-sm font-medium mb-4">Funcionalidades LoRa</h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <FormField
                control={control}
                name="config.specific.wtr.a"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tempo sem conexão</FormLabel>
                    <FormControl>
                      <Input placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.lock_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ativação do rádio</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      defaultValue={field.value?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um tipo de bloqueio" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">Sempre ativo</SelectItem>
                        <SelectItem value="2">Apenas em alertas</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="config.specific.wtr.c"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tempo de bloqueio de mensagens</FormLabel>
                    <FormControl>
                      <Input placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="config.specific.wtr.d"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Velocidade para retransmissão</FormLabel>
                    <FormControl>
                      <Input placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
